/**
 * Progressive Storefront Hunter enrichment helpers.
 * Discovery renders first; enrichment patches fields without overwriting storefront_price.
 */

export const STOREFRONT_ENRICH_BATCH_SIZE = 8;
export const STOREFRONT_ENRICH_MAX_CONCURRENCY = 5;
export const STOREFRONT_ENRICH_TTL_MS = 10 * 60 * 1000;

export const MARKETPLACE_ENRICH_FIELDS = [
  "buy_box",
  "seller_count",
  "brand",
  "category",
  "demand",
];

export function chunk(values, size) {
  const result = [];
  const n = Math.max(1, Number(size) || 1);
  for (let i = 0; i < (values || []).length; i += n) {
    result.push(values.slice(i, i + n));
  }
  return result;
}

export function productItemId(product) {
  return String(
    product?.walmart_item_id ?? product?.walmartItemId ?? product?.item_id ?? product?.id ?? ""
  ).trim();
}

function positivePrice(value) {
  const n = typeof value === "number" ? value : Number(value);
  return value != null && Number.isFinite(n) && n > 0 ? n : null;
}

/** Item ids on the page whose Storefront Price is still unknown. */
export function missingStorefrontPriceIds(products) {
  const out = [];
  for (const product of products || []) {
    const itemId = productItemId(product);
    if (!itemId || product.storefront_price != null || out.includes(itemId)) continue;
    out.push(itemId);
  }
  return out;
}

/**
 * Apply /seller-offers results: fill only unknown Storefront Prices; a known
 * price is never replaced, and a null result never blanks anything.
 */
export function mergeSellerOfferPrices(products, offersMap) {
  if (!Array.isArray(products) || !offersMap) return products || [];
  return products.map((product) => {
    const offer = offersMap[productItemId(product)];
    if (!offer || product.storefront_price != null) return product;
    const price = positivePrice(offer.storefront_price);
    if (price != null) {
      return {
        ...product,
        storefront_price: price,
        storefront_price_source: offer.storefront_price_source || "marketplace_offer",
        storefront_price_reason: null,
      };
    }
    return {
      ...product,
      storefront_price_source: "unavailable",
      storefront_price_reason: offer.reason || "seller_offer_price_unavailable",
    };
  });
}

export function mergeStorefrontProductPatch(products, patchMap) {
  if (!Array.isArray(products) || !patchMap) return products || [];

  return products.map((product) => {
    const itemId = productItemId(product);
    const patch = patchMap[itemId];
    if (!patch) return product;

    // Never overwrite discovery storefront_price with Buy Box.
    // Never blank existing fields with nulls from a partial/error patch.
    const next = { ...product };
    for (const [key, value] of Object.entries(patch)) {
      if (key === "storefront_price" || key === "enrichment") continue;
      if (value === null || value === undefined) continue;
      // Empty-string enrich must not wipe a known discovery/catalog value.
      if (value === "" && product[key] != null && product[key] !== "") continue;
      // Never regress a known multi-offer Walmart count to a fabricated 1.
      if (
        key === "seller_count" &&
        typeof product.seller_count === "number" &&
        product.seller_count > 1 &&
        value === 1 &&
        patch.offer_count_confident !== true
      ) {
        continue;
      }
      next[key] = value;
    }
    next.storefront_price = product.storefront_price;
    // A missing Storefront Price may be filled only by the seller's own Buy Box,
    // which the backend proves by Walmart seller id (seller_offer_price).
    const sellerOffer = positivePrice(patch.seller_offer_price);
    if (product.storefront_price == null && sellerOffer != null) {
      next.storefront_price = sellerOffer;
      next.storefront_price_source = "enrichment_offer";
    }
    next.enrichment = {
      ...(product?.enrichment || {}),
      ...(patch?.enrichment || {}),
    };
    return next;
  });
}

export function buildEnrichmentBatches(products, batchSize = STOREFRONT_ENRICH_BATCH_SIZE, cache) {
  const missing = [];
  for (const product of products || []) {
    const itemId = productItemId(product);
    if (!itemId) continue;
    if (cache?.get?.(itemId)) continue;
    missing.push(itemId);
  }
  return chunk([...new Set(missing)], batchSize);
}

/**
 * Split page products into cache hits (apply immediately) vs IDs to fetch.
 * Prevents marking cache hits as pending then never resolving them.
 */
export function splitEnrichmentTargets(products, cache) {
  const cachedPatches = {};
  const fetchIds = [];
  const seen = new Set();
  for (const product of products || []) {
    const itemId = productItemId(product);
    if (!itemId || seen.has(itemId)) continue;
    seen.add(itemId);
    const hit = cache?.get?.(itemId);
    if (hit) cachedPatches[itemId] = hit;
    else fetchIds.push(itemId);
  }
  return { cachedPatches, fetchIds };
}

export function terminalMarketplaceEnrichment(status) {
  return {
    buy_box: status,
    seller_count: status,
    brand: status,
    category: status,
    demand: status,
  };
}

/** Request succeeded but fields absent — not a Retry state. */
export function unavailableEnrichmentPatch(itemId) {
  return {
    walmart_item_id: String(itemId),
    buy_box_price: null,
    buy_box_owner: null,
    seller_count: null,
    status: "unavailable",
    marketplace_status: "unavailable",
    enrichment: terminalMarketplaceEnrichment("unavailable"),
  };
}

/** Batch / network failure — Retry allowed. */
export function errorEnrichmentPatch(itemId) {
  return {
    walmart_item_id: String(itemId),
    status: "error",
    marketplace_status: "error",
    enrichment: terminalMarketplaceEnrichment("error"),
  };
}

/**
 * After a batch response: every requested ID must become terminal.
 * Missing IDs → unavailable (request finished, field absent) unless error=true.
 */
export function finalizeEnrichmentBatch(requestedIds, itemsMap = {}, { error = false } = {}) {
  const out = {};
  const source = itemsMap && typeof itemsMap === "object" ? itemsMap : {};
  for (const rawId of requestedIds || []) {
    const itemId = String(rawId || "").trim();
    if (!itemId) continue;
    if (error) {
      out[itemId] = errorEnrichmentPatch(itemId);
      continue;
    }
    const patch = source[itemId];
    if (patch && typeof patch === "object") {
      out[itemId] = {
        ...patch,
        enrichment: {
          ...terminalMarketplaceEnrichment("unavailable"),
          ...(patch.enrichment || {}),
        },
      };
    } else {
      out[itemId] = unavailableEnrichmentPatch(itemId);
    }
  }
  return out;
}

/**
 * Client-side freshness policy. Mirrors the backend TTL defaults so a revisit
 * does not re-run provider work for fields that are still fresh.
 * Values are milliseconds.
 */
export const SFH_TTL_MS = {
  identity: 7 * 24 * 60 * 60 * 1000,
  storefront_price: 6 * 60 * 60 * 1000,
  buy_box: 3 * 60 * 60 * 1000,
  seller_count: 6 * 60 * 60 * 1000,
  buy_sources: 8 * 60 * 60 * 1000,
};

/** Latest authoritative check timestamp on a product, in ms (or null). */
export function lastCheckedMs(product) {
  const candidates = [product?.last_checked, product?.marketplace_checked_at];
  let best = null;
  for (const value of candidates) {
    const ms = Date.parse(value || "");
    if (Number.isFinite(ms) && (best == null || ms > best)) best = ms;
  }
  return best;
}

/**
 * Field-level terminality for the two decision-critical marketplace fields.
 *
 * A group-level cache state alone must never leave a field pending forever: a
 * FRESH cache document can still be missing Buy Box and seller count (e.g. an
 * identity-only write, or an older partial document).
 */
const TERMINAL_ENRICHMENT = new Set(["complete", "unavailable", "unsupported", "error"]);

export function buyBoxTerminal(product) {
  const price = product?.buy_box_price;
  const owner = product?.buy_box_owner;
  if ((price != null && Number.isFinite(Number(price))) ||
      (typeof owner === "string" && owner.trim() !== "")) {
    return true;
  }
  return TERMINAL_ENRICHMENT.has(product?.enrichment?.buy_box);
}

export function sellersTerminal(product) {
  if (typeof product?.seller_count === "number" && Number.isFinite(product.seller_count)) {
    return true;
  }
  return TERMINAL_ENRICHMENT.has(product?.enrichment?.seller_count);
}

/** Both required marketplace fields have terminal evidence. */
export function marketplaceFieldsComplete(product) {
  return buyBoxTerminal(product) && sellersTerminal(product);
}

/**
 * True only when the backend served this row from a FRESH cache entry.
 *
 * A STALE_USABLE row must stay revalidatable: an identity-only row (cached when
 * the Walmart render was for a different store) holds brand/category but no
 * location-sensitive value, so treating it as settled would leave the Buy Box
 * permanently blank for that store.
 */
export function servedFreshFromCache(product) {
  return product?.cache_state === "FRESH";
}

/** True when the backend served this row from cache (fresh or stale). */
export function servedFromCache(product) {
  return product?.served_from_cache === true || product?.cache_state === "FRESH";
}

/**
 * Does this product still need a marketplace enrichment request?
 *
 * A FRESH group state does NOT suppress a request while a required field lacks
 * terminal evidence — that is the bug class that left Buy Box / Sellers showing
 * "Loading…" indefinitely. Even a FRESH document is incomplete if Buy Box and
 * seller count have no value and no authoritative-unavailable/error evidence.
 */
export function needsMarketplaceEnrichment(product, now = Date.now(), ttlMs = SFH_TTL_MS.buy_box) {
  if (!product) return true;
  const state = product?.cache_state;
  // An authoritative negative is terminal until the backend's short TTL expires.
  if (state === "NEGATIVE") return false;
  if (!marketplaceFieldsComplete(product)) return true;
  if (state === "FRESH") return false;
  if (state === "STALE_USABLE") return true;
  const checked = lastCheckedMs(product);
  if (checked == null) return false;
  return now - checked >= ttlMs;
}

/**
 * True when the product's marketplace data is still within the Buy Box window.
 * Kept for callers that want the freshness question specifically; revalidation
 * eligibility is `needsMarketplaceEnrichment`.
 */
export function isMarketplaceFresh(product, now = Date.now(), ttlMs = SFH_TTL_MS.buy_box) {
  return !needsMarketplaceEnrichment(product, now, ttlMs);
}

/**
 * Item ids that still need a provider fetch: never checked, past the TTL, or
 * missing a required field that has no terminal evidence.
 */
export function idsNeedingMarketplaceRefresh(products, now = Date.now(), ttlMs = SFH_TTL_MS.buy_box) {
  const out = [];
  for (const product of products || []) {
    const itemId = productItemId(product);
    if (!itemId || out.includes(itemId)) continue;
    if (!needsMarketplaceEnrichment(product, now, ttlMs)) continue;
    out.push(itemId);
  }
  return out;
}

/**
 * Per-field "does this product already have a usable value?" map.
 * Drives the refresh UI: a field WITH a value keeps rendering it while the
 * refresh runs; only a field with no value shows a skeleton.
 */
const FIELD_HAS_VALUE = {
  buy_box: (p) =>
    (p?.buy_box_price != null && Number.isFinite(Number(p.buy_box_price))) ||
    (typeof p?.buy_box_owner === "string" && p.buy_box_owner.trim() !== ""),
  seller_count: (p) => typeof p?.seller_count === "number" && Number.isFinite(p.seller_count),
  brand: (p) => p?.brand != null && p.brand !== "",
  category: (p) => p?.category != null && p.category !== "",
};

/**
 * Mark only the fields that have NO usable value yet as pending.
 *
 * This is the "never blank good data" rule: a known $8.98 Buy Box stays on
 * screen while it revalidates. Replaces markProductsEnrichmentPending on the
 * automatic/background paths.
 */
export function markRefreshableEnrichment(
  products,
  fields = MARKETPLACE_ENRICH_FIELDS,
  onlyItemIds = null
) {
  const allow =
    onlyItemIds == null
      ? null
      : new Set([...(onlyItemIds || [])].map((id) => String(id || "").trim()).filter(Boolean));

  return (products || []).map((p) => {
    const itemId = productItemId(p);
    if (!itemId) return p;
    if (allow && !allow.has(itemId)) return p;
    const enrichment = { ...(p.enrichment || {}) };
    let changed = false;
    for (const field of fields) {
      const hasValue = FIELD_HAS_VALUE[field] ? FIELD_HAS_VALUE[field](p) : false;
      if (hasValue) {
        // Value stays visible. Mark it refreshing only when it is not already
        // complete — a complete field is never regressed to a loading state.
        if (enrichment[field] !== "complete" && enrichment[field] !== "refreshing") {
          enrichment[field] = "refreshing";
          changed = true;
        }
      } else if (enrichment[field] !== "complete") {
        if (enrichment[field] !== "pending") {
          enrichment[field] = "pending";
          changed = true;
        }
      }
    }
    return changed ? { ...p, enrichment } : p;
  });
}

export function countMarketplaceLoading(products) {
  let n = 0;
  for (const p of products || []) {
    const e = p?.enrichment || {};
    if (
      e.buy_box === "pending" ||
      e.buy_box === "loading" ||
      e.seller_count === "pending" ||
      e.seller_count === "loading"
    ) {
      n += 1;
    }
  }
  return n;
}

export async function runWithConcurrency(items, limit, worker) {
  const queue = [...(items || [])];
  const runners = Array.from(
    { length: Math.min(Math.max(1, limit), Math.max(queue.length, 1)) },
    async () => {
      while (queue.length) {
        const item = queue.shift();
        if (item == null) break;
        await worker(item);
      }
    }
  );
  await Promise.all(runners);
}

const RETRYABLE_DIAGNOSTIC_REASONS = new Set([
  "timeout",
  "response_unusable",
  "parser_miss",
  "provider_error",
  "exception",
]);

/**
 * Mirror backend is_terminal_cacheable — do not terminal-cache transient failures.
 */
export function isTerminalCacheableEnrichmentPatch(patch) {
  if (!patch || typeof patch !== "object") return false;
  if (patch.status === "error" || patch.marketplace_status === "error") return false;

  const quality = patch.quality && typeof patch.quality === "object" ? patch.quality : {};
  const request = quality.request && typeof quality.request === "object" ? quality.request : {};
  const response = quality.response && typeof quality.response === "object" ? quality.response : {};
  if (["timeout", "provider_error", "exception"].includes(request.request_status)) {
    return false;
  }
  if (["empty_content", "parse_failed", "invalid"].includes(response.response_status)) {
    return false;
  }

  const diags = patch.field_diagnostics;
  if (diags && typeof diags === "object" && Object.keys(diags).length) {
    const retryReasons = RETRYABLE_DIAGNOSTIC_REASONS;
    let anyFound = false;
    let onlyRetryable = true;
    for (const fd of Object.values(diags)) {
      if (!fd || typeof fd !== "object") continue;
      const reason = fd.reason;
      if (reason === "found") {
        anyFound = true;
        onlyRetryable = false;
      } else if (["absent_in_source", "unsupported", "not_attempted"].includes(reason)) {
        onlyRetryable = false;
      } else if (!retryReasons.has(reason)) {
        onlyRetryable = false;
      }
    }
    if (onlyRetryable && !anyFound) return false;
    if (!anyFound) {
      for (const fd of Object.values(diags)) {
        if (fd && typeof fd === "object" && fd.reason === "parser_miss") return false;
      }
    }
  }

  const enrichment = patch.enrichment && typeof patch.enrichment === "object" ? patch.enrichment : {};
  const active = Object.values(enrichment).filter((v) => v !== "idle" && v != null);
  if (active.length && active.every((v) => v === "error")) return false;

  return true;
}

export function createEnrichmentCache(ttlMs = STOREFRONT_ENRICH_TTL_MS) {
  const map = new Map();
  return {
    get(itemId) {
      const entry = map.get(String(itemId));
      if (!entry) return null;
      if (entry.expiresAt <= Date.now()) {
        map.delete(String(itemId));
        return null;
      }
      return entry.value;
    },
    set(itemId, value) {
      if (!isTerminalCacheableEnrichmentPatch(value)) return;
      map.set(String(itemId), { value, expiresAt: Date.now() + ttlMs });
    },
    clear() {
      map.clear();
    },
  };
}

/**
 * Mark products as pending enrichment for requested fields.
 * When onlyItemIds is provided, only those IDs are marked pending.
 */
export function markProductsEnrichmentPending(
  products,
  fields = MARKETPLACE_ENRICH_FIELDS,
  onlyItemIds = null
) {
  const allow =
    onlyItemIds == null
      ? null
      : new Set([...(onlyItemIds || [])].map((id) => String(id || "").trim()).filter(Boolean));

  return (products || []).map((p) => {
    const itemId = productItemId(p);
    if (!itemId) return p;
    if (allow && !allow.has(itemId)) return p;
    const enrichment = { ...(p.enrichment || {}) };
    for (const field of fields) {
      if (enrichment[field] !== "complete") enrichment[field] = "pending";
    }
    return { ...p, enrichment };
  });
}
